import type { Vec2 } from './vec2';
import { orient2d } from './predicates';
import { GEOMETRY_EPSILON } from '@/constants/numeric';

/** Twice the signed area of a simple polygon. Positive = counter-clockwise. */
export function polygonSignedArea2(poly: readonly Vec2[]): number {
  let sum = 0;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum;
}

export const polygonArea = (poly: readonly Vec2[]): number =>
  Math.abs(polygonSignedArea2(poly)) / 2;

export const isCounterClockwise = (poly: readonly Vec2[]): boolean =>
  polygonSignedArea2(poly) > 0;

export function polygonCentroid(poly: readonly Vec2[]): Vec2 {
  const a2 = polygonSignedArea2(poly);
  if (Math.abs(a2) < GEOMETRY_EPSILON) {
    // Degenerate: fall back to the vertex average.
    let sx = 0;
    let sy = 0;
    for (const p of poly) {
      sx += p.x;
      sy += p.y;
    }
    return { x: sx / poly.length, y: sy / poly.length };
  }
  let cx = 0;
  let cy = 0;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % n];
    const cross = p.x * q.y - q.x * p.y;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  }
  return { x: cx / (3 * a2), y: cy / (3 * a2) };
}

/** Winding-number point-in-polygon test. Boundary counts as inside. */
export function pointInPolygon(p: Vec2, poly: readonly Vec2[]): boolean {
  let winding = 0;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    if (a.y <= p.y) {
      if (b.y > p.y && orient2d(a, b, p) > 0) winding++;
    } else if (b.y <= p.y && orient2d(a, b, p) < 0) {
      winding--;
    }
  }
  return winding !== 0;
}

/** Axis-aligned bounding box of a point set. */
export function boundingBox(points: readonly Vec2[]): {
  min: Vec2;
  max: Vec2;
  width: number;
  height: number;
} {
  if (points.length === 0) {
    return { min: { x: 0, y: 0 }, max: { x: 0, y: 0 }, width: 0, height: 0 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return {
    min: { x: minX, y: minY },
    max: { x: maxX, y: maxY },
    width: maxX - minX,
    height: maxY - minY,
  };
}
